import Vue from 'vue';
import rpcHeaders from '#/store/headers';
import { queryProjectClient, modifyClient, deletionClient } from '#/clients';
import { FolderType, JobInfoQueryExpansionRequest } from '#/protobuf/job_pb';
import { JobGetRequest } from '#/protobuf/service_query_pb';
import { JobTreeItem, createFolderTreeItem } from '#/store/jobs/JobTreeItem';
import { EntityInfoExpansionRequest } from '#/protobuf/entity_pb';

export function emptyState() {
  return {
    jobMap: {},
    jobProtos: {},
  };
}

export const getters = {
  roots: (state) => (id) => {
    const rootTreeItems = [];
    try {
      console.info('ROOTS', id);
      const job = state.jobProtos[id];
      if (!job) {
        console.warn('roots: WARNING: rootEntitiesForJobId found no job for jobId', id);
        return rootTreeItems;
      }
      const rootIds = job.getExpandedJobFolderRootIdsList();
      const jobFolderMap = job.getExpandedJobFoldersMap();
      if (!rootIds || !jobFolderMap) {
        console.warn('jobtree: WARNING: rootEntitiesForJobId could not expand root of folder map', id);
        return rootTreeItems;
      }
      rootIds.forEach((rId) => {
        const folder = jobFolderMap.get(rId);
        if (!folder) {
          console.warn('could not find referenced root folder - ', rId);
          return; // this returns from the forEach - not the entire getter - not exactly idiomatic
        }
        switch (folder.getType()) {
          case FolderType.FOLDER_ROOT_WRAPPER:
            console.info('jobtree: root file wrapper', folder.toObject());
            folder.getChildFiles().getFilesList().forEach((node) => {
              console.info('jobtree: root file', node);
              rootTreeItems.push(new JobTreeItem(node, false));
            });
            break;
          case FolderType.FOLDER_ROOT_FOLDER:
            console.info('jobtree: root folder', folder);
            // TODO: lazy expansion -> we lose counting things like child tags
            rootTreeItems.push(new JobTreeItem(folder.getInfo(), true));
            // rootTreeItems.push(createFolderTreeItem(jobFolderMap, rId));
            break;
          default:
            console.warn('jobtree: WARNING: invalid root folder type', folder.getType());
        }
      });
    } catch (e) {
      console.error('roots: caught ERROR:', e);
    }
    return rootTreeItems;
  },
  childrenOf: (state) => (jobId, folderId) => {
    const children = [];
    try {
      console.info('expanding children of: ', jobId, folderId);
      const job = state.jobProtos[jobId];
      if (!job) {
        console.warn('could not find job to expand children of job', jobId, 'folder', folderId);
        return children;
      }
      const jobFolderMap = job.getExpandedJobFoldersMap();
      if (!jobFolderMap) {
        console.warn('could not retrieve expanded job folder map for job', jobId, 'folder', folderId);
        return children;
      }

      const jobFolder = jobFolderMap.get(folderId);
      console.info('jobFolder to expand:', jobFolder.toObject());
      if (!jobFolder) {
        console.warn('could not get folder to expand for job', jobId, 'folder', folderId);
        return children;
      }

      // add child files
      jobFolder.getChildFiles().getFilesList().forEach((node) => {
        const treeItem = new JobTreeItem(node, false);
        children.push(treeItem);
        console.info('job-tree: expanded child file', treeItem);
      });

      // add child folders
      const childDirs = jobFolder.getChildDirs();
      if (childDirs && childDirs.getChildDirStartNodeId() > 0 && childDirs.getChildDirEndNodeId() > 0) {
        for (let i = childDirs.getChildDirStartNodeId(); i <= childDirs.getChildDirEndNodeId(); i += 1) {
          const childFolder = jobFolderMap.get(i);
          if (!childFolder) {
            console.warn('WARNING: no childFolder for declared index', i);
            // eslint-disable-next-line no-continue
            continue;
          }
          const treeItem = new JobTreeItem(childFolder.getInfo(), true);
          children.push(treeItem);
          console.info('job-tree: expanded child folder', treeItem);
        }
      }
      // todo sort?
    } catch (e) {
      console.error('childrenOf: caught ERROR:', e);
    }
    return children;
  },
  // rootEntitiesForJobId: (state) => (id) => {
  //   console.info('jobtree: calculating rootEntitiesForJobId', id);
  //   const job = state.jobProtos[id];
  //   if (!job) {
  //     console.warn('jobtree: WARNING: rootEntitiesForJobId found no job for jobId', id);
  //     return null;
  //   }
  //   const rootIds = job.getExpandedJobFolderRootIdsList();
  //   const jobFolderMap = job.getExpandedJobFoldersMap();
  //   if (!rootIds || !jobFolderMap) {
  //     console.warn('jobtree: WARNING: rootEntitiesForJobId could not expand root of folder map', id);
  //     return null;
  //   }
  //   const rootTreeItems = [];
  //   rootIds.forEach((rId) => {
  //     const folder = jobFolderMap.get(rId);
  //     switch (folder.getType()) {
  //       case FolderType.FOLDER_ROOT_WRAPPER:
  //         console.info('jobtree: root file wrapper', folder.toObject());
  //         folder.getChildFiles().getFilesList().forEach((node) => {
  //           console.info('jobtree: root file', node);
  //           rootTreeItems.push(new JobTreeItem(node, false));
  //         });
  //         break;
  //       case FolderType.FOLDER_ROOT_FOLDER:
  //         console.info('jobtree: root folder', folder);
  //         // TODO: lazy expansion -> but we lose counting things like child tags
  //         // rootTreeItems.push(new JobTreeItem(folder.getInfo(), true));
  //         rootTreeItems.push(createFolderTreeItem(jobFolderMap, rId));
  //         break;
  //       default:
  //         console.warn('jobtree: WARNING: invalid root folder type', folder.getType());
  //     }
  //   });
  //   return rootTreeItems;
  // },
  jobById: (state) => (jobId) => state.jobMap[jobId],
  // jobById: (state) => (jobId) => state.jobProtos[jobId],
};

export const mutations = {
  setJobDetails: (state, jobInfoQueryProto) => {
    const jobDetails = jobInfoQueryProto.toObject();
    console.info('setJobDetails:', jobDetails.jobId, jobDetails);
    Vue.set(state.jobMap, jobDetails.jobId, jobDetails);
    Vue.set(state.jobProtos, jobDetails.jobId, jobInfoQueryProto);
  },
};

export const actions = {
  loadList: async ({ rootState }, request) => {
    console.log('loading job page', request);
    try {
      const headers = await rpcHeaders(rootState);

      return new Promise((resolve, reject) => {
        queryProjectClient.listJobs(request, headers, (err, response) => {
          if (err != null) {
            console.log('received err from listJobs ', err);
            return reject(err);
          }
          return resolve(response.toObject());
        });
      });
    } catch (error) {
      console.log('caught error loading job page:', error);
      return Promise.reject(error);
    }
  },
  loadDetails: async ({ state, commit, rootState }, request) => {
    console.log('loading job details', request);
    try {
      const headers = await rpcHeaders(rootState);
      return new Promise((resolve, reject) => {
        queryProjectClient.getJobs(request, headers, (err, response) => {
          if (err != null) {
            console.log('received err from getJobs ', err);
            return reject(err);
          }
          // TODO: decide which way wins here -> toObject() or not toObject()
          response.getJobsList().forEach((jobProto) => {
            commit('setJobDetails', jobProto);
            // commit('setJobDetails', jobProto.toObject());
          });

          // const r = response.toObject();
          // r.jobsList.forEach((job) => {
          //   commit('setJobDetails', job);
          // });

          return resolve(response.toObject());
        });
      });
    } catch (error) {
      console.log('caught error loading job page:', error);
      return Promise.reject(error);
    }
  },
  updateTags: async ({ rootState }, request) => {
    console.log('updating tags for job', request);
    try {
      const headers = await rpcHeaders(rootState);
      return new Promise((resolve, reject) => {
        modifyClient.updateJobTags(request, headers, (err, response) => {
          if (err != null) {
            console.log('received err from updateJobTags ', err);
            return reject(err);
          }
          const r = response.toObject();
          return resolve(r);
        });
      });
    } catch (error) {
      console.log('caught error updating job tags:', error);
      return Promise.reject(error);
    }
  },
  delete: async ({ rootState }, request) => {
    console.log('deleting job', request);
    try {
      const headers = await rpcHeaders(rootState);
      return new Promise((resolve, reject) => {
        deletionClient.deleteJobs(request, headers, (err, response) => {
          if (err != null) {
            console.log('received err from job deletion', err);
            return reject(err);
          }
          const r = response.toObject();
          return resolve(r);
        });
      });
    } catch (error) {
      console.log('caught error in job deletion:', error);
      return Promise.reject(error);
    }
  },
};

export default {
  namespaced: true,
  state: emptyState(),
  getters,
  mutations,
  actions,
};
